<template>
    <div
    ref="searchHeaderWrapper"
    class="search-header-wrapper"
    v-click-outside="closeSearchHandler"
    :class="{ 'small-button-variant': smallButton }">
        <!-- Small button to trigger search -->
		<button-secondary
        v-if="smallButton"
        buttonStyle="round"
        buttonSize="small"
        icon="search"
        @clicked="showMobileSearch = true"/>
        <!-- Input wrapper -->
        <div
        v-else
        class="search-animation-wrapper"
        :class="{'position-absolute': appyPositionAbsolute}"
        :style="searchAnimationWrapperStyles">
            <inputs-search
            :preventInputClick="getMobileAppState"
            :params="{ placeholder: t('menu.headerMenu.searchPlaceholder')}"
            :inputValue="searchValue"
            @input="searchValue = $event"
            @onFocus="getMobileAppState ? null : openSearchHandler()"
            @clicked="clickOnSearchHandler"/>
        </div>
        <transition appear name="opening">
            <div v-if="showSearchBody" class="list-body-wrapper">
                <div v-if="filteredMenu.length" class="itens-wrapper" :key="`searchValue${searchValue}`">
                    <div
                    v-for="entry in filteredMenu"
                    class="entry"
                    :key="entry.id"
                    :to="entry.link"
                    @click="[closeSearchHandler(), navigateToRoute(entry)]">
                        <component :is="iconComponent(entry.label)" :class="{'in-search': searchValue.length}"/>
                        <span ref="rowText" class="row-text">{{ entry.name }}</span>
                    </div>
                </div>
                <empty-state-generic
                v-else
                illustration="emptyState/search"
                :title="t('emptyStates.noResults')"
                :description="t('emptyStates.noResultsWithFiltersDescription')"/>
            </div>
        </transition>
    </div>
    <!-- Search Modal for Mobile -->
    <modal-side-search
    v-if="showMobileSearch"
    clearSearchValueOnClose
    :searchPlaceholder="t('menu.headerMenu.searchPlaceholder')"
    :searchInputValue="searchValue"
    :triggerClose="triggerMobileSearchClose"
    :applyEmptyState="!filteredMenu.length"
    @updateSearchValue="searchValue = $event"
    @hideMe="[showMobileSearch = false, triggerMobileSearchClose = false, searchValue = '']">
        <div class="itens-wrapper" :key="`searchValue${searchValue}`">
            <div
            v-for="entry in filteredMenu"
            class="entry"
            :key="entry.id"
            :to="entry.link"
            @click="[triggerMobileSearchClose = true, navigateToRoute(entry)]">
                <component :is="iconComponent(entry.label)" :class="{'in-search': searchValue.length}"/>
                <span ref="rowText" class="row-text">{{ entry.name }}</span>
            </div>
        </div>
    </modal-side-search>
</template>

<script setup>
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import InputsSearch from '@/components/generic/inputs/Search'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ModalSideSearch from '@/components/generic/modals/side/Search'

/* Vue & Assets */
import { ref, computed, watch, defineAsyncComponent } from 'vue'
import { menuIconMapper } from '@/assets/scripts/mappers/menu'
import { parseMenuForHeaderSearch } from '@/assets/scripts/parsers/menu'
import { paymentsFlowsRoute } from '@/assets/scripts/mappers/payments'
import { navigateByPath, navigateByName } from '@/router/helpers/navigation'
import { nextTick } from 'process'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['selectHome'])

/** props received
@smallButton { Boolean } prop to show search with small button
*/
const props = defineProps({
    smallButton: Boolean
})

/* mapGetters */
const { getMenu } = mapGetters(moduleMapper.MENU)
const { getPaymentsOperatorsList } = mapGetters(moduleMapper.PAYMENTS_SERVICES)
const { getMobileAppState } = mapGetters()

/*************************************************
*												 *
*           Open Close Animation Logic           *
*												 *
*************************************************/
const searchHeaderWrapper = ref('')
const appyPositionAbsolute = ref(false)
const showMobileSearch = ref(false)
const showSearchBody = ref(false)
const triggerMobileSearchClose = ref(false)
const searchValue = ref('')
const searchAnimationWrapperStyles = ref(null)

const openSearchHandler = $event => {
    searchAnimationWrapperStyles.value = {
        width: `${searchHeaderWrapper.value.clientWidth}px`,
        left: `${searchHeaderWrapper.value.offsetLeft}px`
    }
    appyPositionAbsolute.value = true
    showSearchBody.value = true

    setTimeout(() => {
        searchAnimationWrapperStyles.value = {
            width: 'calc(100% - 48px)',
            left: '24px'
        }
    }, 10)
}

const closeSearchHandler = () => {
    if (!showSearchBody.value) return

    showSearchBody.value = false
    searchAnimationWrapperStyles.value = {
        width: `${searchHeaderWrapper.value.clientWidth}px`,
        left: `${searchHeaderWrapper.value.offsetLeft}px`
    }

    setTimeout(() => {
        appyPositionAbsolute.value = false
        searchAnimationWrapperStyles.value = null
        searchValue.value = ''
    }, 400)
}

const navigateToRoute = entry => {
    if (entry.link === '/home' && getMobileAppState.value) {
        navigateByPath('/mobileMenuhome')
        emit('selectHome')
    } else if (entry.paymentOperator) {
        navigateByName(paymentsFlowsRoute(entry.paymentOperator.flow), { params: entry.paymentOperator })
    } else navigateByPath(entry.firstTabLink ? entry.firstTabLink : entry.link)
}

/*************************************************
*												 *
*                Search list Logic               *
*												 *
*************************************************/
const unFilteredMenuEntrys = computed(() => {
    return JSON.parse(JSON.stringify(parseMenuForHeaderSearch(getMenu.value, getPaymentsOperatorsList.value)))
})
const rowText = ref()

/* Computed for icon */
const iconComponent = (label) => {
	return defineAsyncComponent(() => import('@/assets/icons/' + menuIconMapper(label) + '.svg?inline'))
}

const filteredMenu = computed(() => {
    if (!searchValue.value.length) return unFilteredMenuEntrys.value

    return unFilteredMenuEntrys.value.filter(option => option.name.toLowerCase().includes(searchValue.value.toLowerCase()))
})

watch(searchValue, () => {
    if (!searchValue.value) {
        nextTick(() => {
            rowText.value.forEach(span => {
                span.innerHTML = span.innerText
            })
        })
        return
    }

    nextTick(() => {
        var regEx = RegExp(searchValue.value, 'gi')
        rowText.value.forEach(span => {
            span.innerHTML = span.innerText.replaceAll(regEx, word => {
                return `<span style="font-weight: 600; color: var(--primary)">${word}</span>`
            })
        })
    })
})

const clickOnSearchHandler = () => {
    if (getMobileAppState) showMobileSearch.value = true
}
</script>

<style lang="scss" scoped>
.search-header-wrapper {
    width: 100%;

    .search-animation-wrapper {
        position: relative;

        transition: all 0.3s ease-in-out;
        z-index: 1;

        &.position-absolute {
            position: absolute;
        }
    }

    .list-body-wrapper {
        @include generic-scrollbar(transparent, var(--primary));

        width: calc(100% - 48px);
        height: auto;
        max-height: 336px;
        padding: 8px 0;

        position: absolute;
        left: 24px;
        top: 56px;

        box-shadow: var(--box-shadow-2);
        background-color: var(--secondary-light-4);
        border-radius: 8px;
        z-index: 3;

        transition: all 0.3s ease-in-out;

        &.opening-enter-to,
        &.opening-leave-from {
            opacity: 1;
        }

        &.opening-enter-from,
        &.opening-leave-to {
            opacity: 0;
        }

        .itens-wrapper {
            &:not(.mobile) {
                @include generic-scrollbar(transparent, var(--primary));
            }

            width: 100%;
            height: auto;
            padding: 8px 0;

        }
    }

    &.small-button-variant {
        width: 40px;
    }
}

.entry {
    width: 100%;
    height: 40px;

    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;

    padding: 0 16px;

    @include H7-Medium;
    color: var(--secondary-dark-2);

    cursor: pointer;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: var(--button-secondary-state-hover);
        }
    }

    &:active {
        background-color: var(--button-secondary-state-press);
    }

    svg {
        width: 16px;
        height: 16px;

        flex-shrink: 0;

        :deep(.change-color-fill) {
            fill: var(--secondary);
        }

        &.in-search {
            :deep(.change-color-fill) {
                fill: var(--primary);
            }
        }
    }

    .row-text {
        text-transform: none;
        text-overflow: ellipsis;
        white-space: nowrap;

        position: relative;
        overflow: hidden;
    }
}
</style>
